import {
  Button,
  Column,
  Columns,
  FlexBox,
  Icon,
  Page,
  Spinner,
  TabItem,
  Tabs,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { AppLink } from 'components/appLink';
import { BulletinBoardContext } from 'components/settings/components/sections/dixaMessenger/views/settings/bulletinBoardContext';
import {
  GeneralSettings,
  MessengerPreview,
  Setup,
  StyleSettings,
} from 'components/settings/components/sections/dixaMessenger/views/settings/components';
import { MessengerMode } from 'components/settings/components/sections/dixaMessenger/views/settings/components/messengerMode/messengerMode';
import {
  useBulletins,
  useMessengerPreview,
} from 'components/settings/components/sections/dixaMessenger/views/settings/hooks';
import {
  FormValues,
  ThemeColor,
} from 'components/settings/components/sections/dixaMessenger/views/settings/types';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { messengerSettingsActions } from '../../messengerSettings.actions';
import { selectKnowledgeState } from '../../messengerSettings.selectors';

import { Authentication, Knowledge } from './components';
import styles from './messengerSettingsPage.module.scss';

type Props = {
  messengerId: string;
  messengerToken: string;
  messengerName?: string;
  onSubmit(value: FormValues): void;
  presetBackgrounds: ThemeColor[];
  isLoading?: boolean;
  bulletinBoardId: string;
  avatars: string[];
  initialValues: Partial<FormValues>;
};

export function MessengerSettingsPage({
  avatars,
  bulletinBoardId,
  messengerId,
  messengerToken,
  presetBackgrounds,
  onSubmit,
  messengerName,
  isLoading = false,
  initialValues,
}: Props): JSX.Element {
  const isLeftMenuOpen = useAppSelector(
    (state) => state.settings.personal.isLeftMenuOpen,
  );

  const formManager = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'all',
  });

  const { control, handleSubmit, reset, formState } = formManager;

  const { isDirty, isValidating } = formState;

  const formValues = useWatch({ control });

  const { hasCustomFormsV2Feature } = useFeatureFlag([
    'has-custom-forms-v2-feature',
  ]);

  const tabs = (
    [
      'General',
      ...(hasCustomFormsV2Feature ? ['Initial state'] : []),
      'Authentication',
      'Knowledge',
      'Styling',
      'Setup',
    ] as const
  ).map((label) => ({
    id: label,
    label,
  })) satisfies TabItem[];

  type Tabs = (typeof tabs)[number]['id'];

  const bulletinAPI = useBulletins({
    boardId: bulletinBoardId,
    messengerId,
    onBeforeAdd() {
      if (!isDirty) return;
      onSubmit(formValues as FormValues);
    },
  });
  const knowledgeState = useAppSelector(selectKnowledgeState);
  const dispatch = useAppDispatch();

  const { ref, loaded, onLoad, setPreviewLanguage } = useMessengerPreview(
    formValues,
    bulletinAPI.bulletins,
    avatars,
    knowledgeState,
  );

  const isSmallScreen = useBreakpointValue({ base: true, lg: false });

  const [activeTab, setActiveTab] = useState(tabs[0].id);

  const handleOnCancel = () => {
    reset();
    dispatch(messengerSettingsActions.reset());
  };

  const isPristine = !isDirty && knowledgeState.isPristine;
  const isLoadingOrValidating = isLoading || Boolean(isValidating);

  return (
    <FormProvider {...formManager}>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <Page>
          <Page.Head
            title={
              <FlexBox gap="200" alignItems="center">
                <AppLink
                  path="/settings/dixa-messenger"
                  style={{ color: 'inherit' }}
                >
                  Messenger
                </AppLink>

                <Icon name="angle-right" />

                {messengerName}
              </FlexBox>
            }
            controls={
              <>
                <Button
                  variant="secondary"
                  type="button"
                  disabled={isPristine || isLoadingOrValidating}
                  onClick={handleOnCancel}
                >
                  Discard changes
                </Button>

                <Button
                  type="submit"
                  disabled={isPristine || isLoadingOrValidating}
                  isLoading={isLoadingOrValidating}
                  loadingIndicator={<Spinner size={16} />}
                >
                  Save changes
                </Button>
              </>
            }
          />

          <Page.Tabs>
            <Tabs
              selectedId={activeTab}
              tabs={tabs}
              onChange={(id) => setActiveTab(id as Tabs)}
            />
          </Page.Tabs>

          <Page.Body>
            <Columns>
              <Column size={isSmallScreen ? '100%' : '60%'}>
                <Column.Row scrollable>
                  {activeTab === 'General' && (
                    <BulletinBoardContext.Provider value={bulletinAPI}>
                      <GeneralSettings
                        setPreviewLanguage={setPreviewLanguage}
                      />
                    </BulletinBoardContext.Provider>
                  )}

                  {activeTab === 'Initial state' && hasCustomFormsV2Feature && (
                    <MessengerMode
                      currentMessengerMode={formValues?.messengerMode}
                    />
                  )}

                  {activeTab === 'Authentication' && (
                    <Authentication
                      messengerId={messengerId}
                      minimumIdentificationLevel={
                        formValues?.minimumIdentificationLevel
                      }
                    />
                  )}

                  {activeTab === 'Knowledge' && (
                    <Knowledge messengerId={messengerId} />
                  )}

                  {activeTab === 'Styling' && (
                    <StyleSettings
                      presetBackgrounds={presetBackgrounds}
                      backgroundColor={
                        formValues?.backgroundColor ?? presetBackgrounds[0].id
                      }
                      logo={formValues?.logo}
                    />
                  )}

                  {activeTab === 'Setup' && (
                    <Setup
                      messengerId={messengerId}
                      messengerToken={messengerToken}
                    />
                  )}
                </Column.Row>
              </Column>

              <Column size={isSmallScreen ? '0' : '40%'}>
                <Column.Row
                  size={1}
                  noVerticalPadding
                  noHorizontalPadding
                  className={styles.preview}
                >
                  <MessengerPreview
                    ref={ref}
                    onLoad={onLoad}
                    className={classNames({ [styles.previewLoading]: !loaded })}
                    isLeftMenuOpen={isLeftMenuOpen}
                  />
                  {!loaded && <Spinner className={styles.previewSpinner} />}
                </Column.Row>
              </Column>
            </Columns>
          </Page.Body>
        </Page>
      </form>
    </FormProvider>
  );
}
